/**
 * API Service para o módulo Book IA.
 *
 * Segue o mesmo padrão do pbtaApi: todas as chamadas passam pelo
 * apiService.request() que injeta o Bearer token do localStorage.
 *
 * Prefixo do backend: /api/modules/book_ia
 */

import { apiService } from '@/services/api'

const BASE = '/modules/book_ia'

// ============================================================================
// Tipos de resposta
// ============================================================================

export interface BookIaConfig {
  id: string
  table_id: string
  llm_provider: string
  llm_model: string
  has_api_key: boolean
  topic_scope: string | null
  forbidden_topics: string[]
  requires_master_approval: boolean
}

export interface ValidateResult {
  ready: boolean
  missing: string[]
}

export interface PlayableResult {
  active: boolean
  ready: boolean
  config_id: string | null
}

export interface LoadResult {
  narrators: number
  npcs: number
  chapters: number
  chapter_npc: number
  chapter_npc_skipped: number
}

export interface ChapterTitle {
  id: string
  order: number
  title: string
}

export interface AdventureSummary {
  config_id: string
  narrators: number
  npcs: number
  chapters: number
  chapter_npc: number
  chapter_titles: ChapterTitle[]
}

export interface PreviewIndex {
  adventures: string[]
}

export interface AdventureValidateResult {
  ok: boolean
  errors: string[]
  title: string | null
  slug: string | null
  narrators: number
  npcs: number
  chapters: number
  chapter_titles: string[]
}

export interface AdventureTemplate {
  filename: string
  content: string
}

export interface BookIaSession {
  id: string
  table_id: string
  config_id: string
  title: string | null
  narrator_id: string | null
  status: string
  created_at: string
}

/**
 * Livro enriquecido para o monitor do professor (GET /sessions/table/{id}).
 * Espelha o SessionMonitorOut do backend — nomes resolvidos na borda, zero PII.
 */
export interface BookIaSessionMonitor {
  id: string
  title: string
  status: string
  owner_id: string
  owner_name: string
  narrator_id: string | null
  narrator_name: string | null
  current_chapter_id: string | null
  current_chapter_title: string | null
  started_at: string | null
}

/**
 * Capítulo template + status de progresso por livro
 * (GET /sessions/{id}/chapters). Espelha o ChapterWithStatusOut do backend.
 */
export interface BookIaChapterStatus {
  id: string
  order: number
  title: string
  blurb: string | null
  era: string | null
  is_required: boolean
  status: string // locked | active | completed
  completed_at: string | null
}

// ============================================================================
// Config
// ============================================================================

export interface CreateConfigPayload {
  table_id: string
  llm_provider?: string
  llm_model?: string
  llm_api_key?: string
  topic_scope?: string
  forbidden_topics?: string[]
  requires_master_approval?: boolean
}

export interface UpdateConfigPayload {
  llm_provider?: string
  llm_model?: string
  llm_api_key?: string
  topic_scope?: string
  forbidden_topics?: string[]
  requires_master_approval?: boolean
}

// ============================================================================
// Cliente
// ============================================================================

export const bookIaApi = {
  // --------------------------------------------------------------------------
  // CONFIG
  // --------------------------------------------------------------------------

  /** Lê config pelo table_id. Lança 404 se não existir. */
  async getConfigByTable(tableId: string): Promise<BookIaConfig> {
    return apiService.request(`${BASE}/config?table_id=${tableId}`, {
      method: 'GET',
    })
  },

  /** Cria configuração para uma mesa (exige professor). */
  async createConfig(payload: CreateConfigPayload): Promise<BookIaConfig> {
    return apiService.request(`${BASE}/config`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /** Atualiza config existente (payload parcial). */
  async updateConfig(configId: string, payload: UpdateConfigPayload): Promise<BookIaConfig> {
    return apiService.request(`${BASE}/config/${configId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },

  /** Verifica prontidão: {ready, missing[]}. (professor) */
  async validateConfig(configId: string): Promise<ValidateResult> {
    return apiService.request(`${BASE}/config/${configId}/validate`, {
      method: 'POST',
    })
  },

  /**
   * Prontidão para JOGAR vista por um membro da mesa (aluno): {active, ready, config_id}.
   * Acessível a qualquer membro — usado pela tela de detalhes para decidir se
   * mostra o botão de entrar e para reabrir uma sessão existente.
   */
  async getPlayable(tableId: string): Promise<PlayableResult> {
    return apiService.request(`${BASE}/config/playable?table_id=${tableId}`, {
      method: 'GET',
    })
  },

  /** Carrega aventura .md no banco para esta config. Idempotente. */
  async loadAdventure(configId: string, slug: string): Promise<LoadResult> {
    return apiService.request(`${BASE}/config/${configId}/adventure/load`, {
      method: 'POST',
      body: JSON.stringify({ slug }),
    })
  },

  /** Resumo da aventura já carregada (capítulos/NPCs/narradores). */
  async getAdventureSummary(configId: string): Promise<AdventureSummary> {
    return apiService.request(`${BASE}/config/${configId}/adventure`, {
      method: 'GET',
    })
  },

  /** Baixa o modelo de aventura .md (instruções + exemplo). */
  async getAdventureTemplate(): Promise<AdventureTemplate> {
    return apiService.request(`${BASE}/adventure/template`, {
      method: 'GET',
    })
  },

  /** Checa a sintaxe de um .md de aventura sem gravar nada. */
  async validateAdventureFile(content: string): Promise<AdventureValidateResult> {
    return apiService.request(`${BASE}/adventure/validate`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    })
  },

  /** Envia + carrega um .md de aventura para esta config. */
  async uploadAdventureFile(
    configId: string,
    content: string,
    filename?: string
  ): Promise<LoadResult> {
    return apiService.request(`${BASE}/config/${configId}/adventure/upload`, {
      method: 'POST',
      body: JSON.stringify({ content, filename }),
    })
  },

  /** Lista slugs de aventuras empacotadas (GET /preview). */
  async listAdventures(): Promise<string[]> {
    const data: PreviewIndex = await apiService.request(`${BASE}/preview`, {
      method: 'GET',
    })
    return data.adventures
  },

  // --------------------------------------------------------------------------
  // SESSIONS (stubs para a peça 2)
  // --------------------------------------------------------------------------

  /** Cria sessão (livro) na mesa. restart=true arquiva os livros in_progress
   *  anteriores do jogador (status -> abandoned), preservando o conteúdo. */
  async createSession(payload: {
    table_id: string
    character_id?: string
    title?: string
    narrator_id?: string
    restart?: boolean
  }): Promise<BookIaSession> {
    return apiService.request(`${BASE}/sessions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /** Lista as sessões do usuário autenticado. */
  async getMySessions(): Promise<BookIaSession[]> {
    return apiService.request(`${BASE}/sessions`, {
      method: 'GET',
    })
  },

  /** Monitor do professor: livros da mesa enriquecidos (aluno, narrador, capítulo). */
  async getTableSessions(tableId: string): Promise<BookIaSessionMonitor[]> {
    return apiService.request(`${BASE}/sessions/table/${tableId}`, {
      method: 'GET',
    })
  },

  /** Capítulos do livro com status de progresso (locked|active|completed). */
  async getSessionChapters(sessionId: string): Promise<BookIaChapterStatus[]> {
    return apiService.request(`${BASE}/sessions/${sessionId}/chapters`, {
      method: 'GET',
    })
  },

  /** Lê uma sessão pelo id. */
  async getSession(sessionId: string): Promise<BookIaSession> {
    return apiService.request(`${BASE}/sessions/${sessionId}`, {
      method: 'GET',
    })
  },

  /** Exclui sessão (professor ou owner). */
  async deleteSession(sessionId: string): Promise<{ message: string; deleted_id: string }> {
    return apiService.request(`${BASE}/sessions/${sessionId}`, {
      method: 'DELETE',
    })
  },
}
